/* eslint-disable max-lines-per-function, max-statements */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ApiError, errorMessage, isQuotaQueuedError, normalizeApiError,
} from '../services/apiError';
import { chatService } from '../services/chatService';
import { eventService } from '../services/eventService';
import type { Project } from '../services/projectService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useGetPlanningStatusQuery } from '../store/services/planningRuntimeApi';
import { setProjectTitleConfirmation } from '../store/slices/planningSlice';
import { reconcileAmbiguousPlanningTrigger } from '../utils/planningTriggerReconciliation';

import { recordActionClick } from './AiOverviewPanel/useActionBarClicks';
import { isPlanningCreditRefused } from './ChatBox/planningCreditRefusalError';
import {
  ACTION_STALE_CODE, EXPLAINED_REFUSAL_CODES, refreshPlanningActions, startFailureCopy,
} from './planningActionsRefresh';
import { revealPlanningConfirmation } from './planningConfirmationVisibility';
import { notifyProductionStart } from './production/productionStartEvents';
import {
  ProjectTitleConfirmation,
  type ProjectTitleConfirmationController,
  useProjectTitleConfirmation,
} from './ProjectTitleConfirmation';
import { useAutoRunFeed, useServerAutoRun } from './serverAutoRun';
import { useToast } from './Toast/Toast';

interface StartRequest {
  actionKey: string;
  buttonKey: string;
  invocationId?: string;
  projectId: string;
  sessionId?: string | null;
  needsTitle: boolean;
  onInvocation: () => Promise<void>;
  onStart: () => Promise<void>;
}

interface PendingStart extends StartRequest {
  invocation: Promise<void>;
  started: boolean;
}

type ChatSurface = 'overview' | 'chat';

/** The current suggestion whose Start will also confirm the project title. */
export interface TitleDraft {
  actionKey: string;
  projectId: string;
  sessionId?: string | null;
  /** The suggestion's timeline entry: the server's Start countdown is keyed to it. */
  entryId?: string | null;
}

/**
 * The server's countdown to pressing Start for the drafted suggestion. The
 * server presses it whether or not a tab is open, so a browser never fires it.
 */
interface AutoStartState {
  /** Seconds left; null when the server will not press Start on its own. */
  secondsLeft: number | null;
  stopped: boolean;
  due: boolean;
}

interface PlanningStartContextValue {
  pending: { actionKey: string; buttonKey: string; invocationId?: string; needsTitle: boolean } | null;
  requestStart: (request: StartRequest) => void;
  confirmation: ProjectTitleConfirmationController | null;
  chatSurface: ChatSurface;
  /** Show the title form ahead of Start for this suggestion (null clears an owner). */
  registerTitleDraft: (draft: TitleDraft | null, actionKey?: string) => void;
  autoStart: AutoStartState;
  /** Wait: asks the server not to press Start on its own. */
  stopAutoStart: () => void;
}

const sameDraft = (left: TitleDraft | null, right: TitleDraft | null): boolean => (
  left === right || Boolean(left && right && left.actionKey === right.actionKey
    && left.projectId === right.projectId && (left.sessionId ?? null) === (right.sessionId ?? null)
    && (left.entryId ?? null) === (right.entryId ?? null))
);

const PlanningStartContext = createContext<PlanningStartContextValue | null>(null);

/** A definitive rejection cannot be retried with the captured request: reload every projection. */
const reloadActions = (): void => window.location.reload();

const isRejectedInvocation = (error: unknown): boolean => {
  const status = (error as { status?: number; response?: { status?: number } })?.status
    ?? (error as { response?: { status?: number } })?.response?.status;

  // A 429 carrying its plan's quota queue is final until the hour resets: never resent.
  return Boolean(status && status >= 400 && status !== 408 && status < 500
    && (status !== 429 || isQuotaQueuedError(error)));
};

/** The server's Start countdown, drawn only; Wait asks the server not to press Start. */
const useServerAutoStart = (projectId: string | null, entryId: string | null, paused: boolean) => {
  const { secondsLeft, wait } = useServerAutoRun(projectId, entryId, 'start');
  const autoStart = useMemo<AutoStartState>(
    () => ({ secondsLeft: paused ? null : secondsLeft, stopped: false, due: false }), [paused, secondsLeft],
  );

  return { autoStart, stopAutoStart: wait };
};

interface AutoRunPress {
  project_id?: string;
  entry_id?: string;
  button_key?: string;
  status?: string;
}

/** The server's timer pressed Start: it confirmed the title it carried, so the card must go. */
const useServerPressedStart = (projectId: string | null, entryId: string | null, onPressed: () => void) => {
  useEffect(() => {
    if (!projectId || !entryId) return undefined;

    return eventService.subscribe('auto_run', (data) => {
      const event = data as AutoRunPress;

      if (event.project_id === projectId && event.entry_id === entryId
        && event.button_key === 'start' && event.status === 'fired') onPressed();
    });
  }, [projectId, entryId, onPressed]);
};

/** Own the confirmation above transcript rows, which may disappear during SSE updates. */
export const PlanningStartProvider: React.FC<React.PropsWithChildren<{
  projectId: string | null;
  sessionId: string | null;
  chatSurface?: ChatSurface;
}>> = ({ projectId, sessionId, chatSurface = 'overview', children }) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const projectTitle = useAppSelector((state) => state.planning.projectTitle);
  const { refetch: refetchPlanningStatus } = useGetPlanningStatusQuery(
    projectId ?? '',
    { skip: !projectId },
  );
  const pendingTitleConfirmation = useAppSelector((state) => (
    state.planning.activeProjectId === projectId
      && state.planning.activeSessionId === sessionId
      ? state.planning.pendingTitleConfirmation
      : null
  ));
  const [pending, setPending] = useState<PendingStart | null>(null);
  const [actionRejected, setActionRejected] = useState(false);
  const [draft, setDraft] = useState<TitleDraft | null>(null);
  const requestRef = useRef<PendingStart | null>(null);
  const serverPressed = useRef(new Set<string>());
  const active = useRef(true);

  useEffect(() => {
    active.current = true;

    return () => {
      active.current = false;
      requestRef.current = null;
    };
  }, []);

  const start = useCallback(async (request: PendingStart) => {
    if (request.started || requestRef.current !== request || !active.current) return;
    request.started = true;
    let finished = false;
    let prepared = false;

    try {
      await request.invocation.catch((error: unknown) => {
        if (isRejectedInvocation(error) || !active.current || requestRef.current !== request) throw error;
        // Save & Start retries the same unacknowledged receipt request. Its
        // callback retains the original request UUID and selected tasks.
        request.invocation = Promise.resolve().then(request.onInvocation);

        return request.invocation;
      });
      prepared = true;
      // Navigation or cancellation must never start the old project's agents.
      if (active.current && requestRef.current === request) {
        await request.onStart();
        finished = true;
        if (active.current && requestRef.current === request) {
          notifyProductionStart({ projectId: request.projectId, startKey: request.actionKey });
        }
      }
    } catch (error) {
      request.started = false;
      const rejected = !prepared && isRejectedInvocation(error);
      const { code, language, kind, status } = normalizeApiError(error);
      // The server explains its own refusals (20 Oct 2026: 26 refused Starts all read "Please try again"),
      // the receipt's and the start request's alike: a 4xx from trigger-agents says why it did not start.
      const triggerRefused = prepared && kind === 'api' && Boolean(status && status < 500);
      const explained = (rejected && (EXPLAINED_REFUSAL_CODES.has(code) || isQuotaQueuedError(error))) || triggerRefused
        ? errorMessage(error) : null;
      const stale = rejected && code === ACTION_STALE_CODE;
      const copy = startFailureCopy(explained ? language : undefined);

      // An out-of-date button: load the step that replaced it, in place.
      if (stale && active.current) {
        refreshPlanningActions(request.projectId);
        void refetchPlanningStatus();
      }
      if (request.needsTitle && active.current && requestRef.current === request) {
        setActionRejected(rejected);
        throw new ApiError({
          code: 'PLANNING_START_NOT_PREPARED',
          message: rejected
            ? [copy.titleSaved, explained, copy.selectRefresh].filter(Boolean).join(' ')
            : `Your title is saved. ${errorMessage(error, 'Planning could not be started.')}`
              + ' Select Save & Start to retry.',
        });
      }
      // A credit refusal is already shown in chat with its Add Credits / Upgrade actions.
      if (active.current && !isPlanningCreditRefused(error)) {
        toast.error(
          explained
            ? [copy.notStarted, explained, stale ? copy.refreshed : null].filter(Boolean).join(' ')
            : 'We couldn’t start planning. Please try again.',
          [{ label: 'Refresh actions', onClick: reloadActions }],
        );
      }
    } finally {
      if (requestRef.current === request && (finished || !request.needsTitle)) {
        requestRef.current = null;
        if (active.current) setPending(null);
      }
    }
  }, [refetchPlanningStatus, toast]);

  const requestStart = useCallback((request: StartRequest) => {
    if (!active.current || requestRef.current || request.projectId !== projectId
      || (request.sessionId && request.sessionId !== sessionId)) return;
    const next = { ...request, invocation: Promise.resolve(), started: false };

    // Claim synchronously before the transcript request can replace the row or
    // another physical click can submit the same original suggestion again.
    requestRef.current = next;
    setActionRejected(false);
    next.invocation = Promise.resolve().then(request.onInvocation);
    void next.invocation.catch(() => {});
    setPending(next);
    if (!next.needsTitle) void start(next);
  }, [projectId, sessionId, start]);

  // Restore before paint so the saved task projection cannot flash in place
  // of the title form while a refreshed project snapshot is being applied.
  useLayoutEffect(() => {
    const restored = pendingTitleConfirmation;

    if (
      !restored
      || !projectId
      || (restored.session_id ?? null) !== (sessionId ?? null)
      || requestRef.current
    ) return;

    requestStart({
      actionKey: JSON.stringify([projectId, restored.session_id ?? null, restored.source_entry_id]),
      buttonKey: restored.button_key,
      invocationId: restored.invocation_id,
      projectId,
      sessionId: restored.session_id,
      needsTitle: true,
      onInvocation: () => Promise.resolve(),
      onStart: async () => {
        try {
          const result = await chatService.triggerAgents(
            projectId,
            restored.agents,
            restored.source_entry_id,
            restored.session_id,
            undefined,
            restored.invocation_id,
          );

          if (result.status !== 'triggered') {
            throw new Error(result.reason || 'No agent run was started.');
          }
        } catch (error) {
          const accepted = await reconcileAmbiguousPlanningTrigger(
            error,
            restored.source_entry_id,
            () => refetchPlanningStatus().unwrap(),
          );

          if (!accepted) throw error;
          toast.info('Planning started. The confirmation response was delayed.');
        }
        await recordActionClick(
          restored.source_entry_id,
          'agent_suggestion',
          restored.button_key,
          projectId,
          restored.session_id,
        );
      },
    });
  }, [pendingTitleConfirmation, projectId, refetchPlanningStatus, requestStart, sessionId, toast]);

  const confirmed = useCallback(async (project: Project) => {
    const request = requestRef.current;

    if (!active.current || !request || request.started || project.id !== request.projectId) return;
    dispatch(setProjectTitleConfirmation(project));
    await start(request);
  }, [dispatch, start]);

  // The draft is the suggestion whose Start doubles as the title submit. It
  // stays the controller's identity through the click itself, so the typed
  // title survives the hand-over into the pending request.
  const registerTitleDraft = useCallback((next: TitleDraft | null, actionKey?: string) => {
    if (!active.current) return;
    // A suggestion the server already started must not bring its title card back.
    if (next?.entryId && serverPressed.current.has(next.entryId)) return;
    if (next && next.projectId !== projectId) return;
    setDraft((prev) => {
      if (next) return sameDraft(prev, next) ? prev : next;

      return actionKey && prev?.actionKey !== actionKey ? prev : null;
    });
  }, [projectId]);

  useAutoRunFeed(projectId);
  const draftEntryId = draft?.entryId ?? null;
  const onServerPressed = useCallback(() => {
    if (draftEntryId) serverPressed.current.add(draftEntryId);
    setDraft(null);
    void refetchPlanningStatus();
  }, [draftEntryId, refetchPlanningStatus]);

  useServerPressedStart(projectId, draftEntryId, onServerPressed);
  // An accepted start hides the countdown; the server stands its own down.
  const { autoStart, stopAutoStart } = useServerAutoStart(
    projectId, draft?.entryId ?? null, Boolean(pending && !pending.needsTitle),
  );

  const inlineTitle = Boolean(draft && (!pending?.needsTitle || pending.actionKey === draft.actionKey));
  const titleOwner = pending?.needsTitle ? pending : draft;
  const confirmation = useProjectTitleConfirmation({
    requestKey: titleOwner?.actionKey ?? null,
    currentTitle: projectTitle,
    projectId: titleOwner?.projectId ?? null,
    sessionId: titleOwner?.sessionId ?? sessionId,
    onConfirmed: confirmed,
    // A definitive rejection cannot be retried with the captured request.
    // Reload all projections and their in-memory selections from server state.
    onRefreshActions: actionRejected ? reloadActions : undefined,
    inline: inlineTitle,
    onInteract: stopAutoStart,
  });
  const value = useMemo(() => ({
    pending, requestStart, chatSurface,
    confirmation: titleOwner ? confirmation : null,
    registerTitleDraft, autoStart, stopAutoStart,
  }), [
    pending, requestStart, chatSurface, confirmation, titleOwner, registerTitleDraft, autoStart, stopAutoStart,
  ]);

  return (
    <PlanningStartContext.Provider value={value}>
      {children}
    </PlanningStartContext.Provider>
  );
};

export const usePlanningStart = (): PlanningStartContextValue => {
  const context = useContext(PlanningStartContext);

  if (!context) throw new Error('Planning actions require PlanningStartProvider');

  return context;
};

/** Read the optional pending request from transcript renderers that may also
 * be mounted in isolated previews outside the planning provider. */
export const usePendingPlanningStart = (): PlanningStartContextValue['pending'] => (
  useContext(PlanningStartContext)?.pending ?? null
);

export const usePlanningStartMessageVisible = (surface: ChatSurface = 'overview'): boolean => {
  const context = useContext(PlanningStartContext);

  return Boolean(context?.confirmation && context.chatSurface === surface);
};

/** A single inline message on the visible chat surface, independent of transcript rows. */
export const PlanningStartMessage: React.FC<{ surface?: ChatSurface }> = ({ surface = 'overview' }) => {
  const context = useContext(PlanningStartContext);
  const messageRef = useRef<HTMLDivElement>(null);
  const visible = Boolean(context?.confirmation && context.chatSurface === surface);

  useEffect(() => {
    const message = messageRef.current;

    if (!visible || !message) return undefined;
    let frame = 0;
    const reveal = () => {
      globalThis.cancelAnimationFrame(frame);
      frame = globalThis.requestAnimationFrame(() => revealPlanningConfirmation(message));
    };

    reveal();
    const observer = globalThis.ResizeObserver ? new globalThis.ResizeObserver(reveal) : null;

    observer?.observe(message);
    const panel = message.closest<HTMLElement>('.ai-overview-panel, .planning-messages');

    if (panel) observer?.observe(panel);
    document.querySelectorAll('.input-section').forEach((element) => observer?.observe(element));

    return () => {
      globalThis.cancelAnimationFrame(frame);
      observer?.disconnect();
      panel?.style.removeProperty('--confirmation-composer-overlap');
    };
  }, [visible]);

  if (!visible || !context?.confirmation) return null;

  return (
    <div ref={messageRef} className="ai-overview-item-wrapper planning-start-message">
      <ProjectTitleConfirmation controller={context.confirmation} />
    </div>
  );
};
